import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost } from '../utils/backendApi';

// Operator view of the email service (/apps/admin/mail/*): planes (shared
// + dedicated Stalwart hosts) with vitals, smart-host and manual-step
// checklist, baseline re-apply, write-only credential; every customer
// domain with its reputation state and an override.

const fmtAgo = (ts) => {
  if (!ts) return '—';
  const m = Math.round((Date.now() - ts) / 60e3);
  return m < 1 ? 'now' : m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`;
};
const REP_TONE = { good: 'up', warmup: 'info', throttled: 'warn', paused: 'down' };

function PlaneCard({ plane, onPatch, onBaseline, onCredential, busy }) {
  const [cred, setCred] = useState('');
  const [credState, setCredState] = useState(null);
  const [warmup, setWarmup] = useState(plane.ips?.warmup || '');
  useEffect(() => {
    apiGet(`/apps/admin/mail/planes/${plane.id}/credential`).then(setCredState).catch(() => setCredState(null));
  }, [plane.id]);
  const s = plane.host?.stats || {};
  const agentUp = plane.host?.agentAt && Date.now() - plane.host.agentAt < 90e3;
  const health = plane.health;
  const cl = plane.checklist || {};
  const ready = cl.port25 && cl.ptr && cl.tls && credState?.exists && health?.ok;
  return (
    <div className="cs-card aa-mail-plane">
      <div className="aa-mail-plane-head">
        <strong className="aa-mono">{plane.id}</strong>
        <span className={`aa-health-badge is-${plane.kind === 'shared' ? 'info' : 'up'}`}>{plane.kind}</span>
        <span className={`aa-health-badge is-${health?.ok ? 'up' : health ? 'down' : 'unknown'}`} title={health?.error || ''}>{health?.ok ? 'reachable' : health ? 'unreachable' : 'unchecked'}</span>
        <span className={`aa-health-badge is-${ready ? 'up' : 'warn'}`}>{ready ? 'production-ready' : 'setup incomplete'}</span>
        <span className="cs-help">{plane.status} · {plane.domains || 0} domain(s) · host {plane.hostId}</span>
      </div>
      <dl className="aa-mail-dl">
        <dt>MX hostname</dt><dd className="aa-mono">{plane.hostname}</dd>
        <dt>Primary IP</dt><dd className="aa-mono">{plane.ips?.primary} <span className="cs-help">PTR → {plane.hostname}</span></dd>
        <dt>Warm-up IP</dt>
        <dd>
          <span className="aa-launch-form">
            <input className="aa-input" value={warmup} onChange={(e) => setWarmup(e.target.value)} placeholder="none — new domains share the primary IP" />
            <button type="button" className="cs-btn cs-btn-sm" disabled={busy || warmup === (plane.ips?.warmup || '')} onClick={() => onPatch(plane.id, { warmupIp: warmup.trim() })}>Save</button>
          </span>
        </dd>
        <dt>Graduated domains</dt><dd>{(plane.graduated || []).length ? plane.graduated.join(', ') : <span className="cs-help">none yet (all on warm-up)</span>}</dd>
        <dt>Host vitals</dt>
        <dd>
          disk {s.diskPct != null ? `${s.diskPct}%` : '—'} · mem {s.memUsedPct != null ? `${s.memUsedPct}%` : '—'} · load {s.load1 ?? '—'} ·{' '}
          <span className={`aa-health-badge is-${agentUp ? 'up' : 'down'}`}>{agentUp ? 'agent polling' : 'agent silent'}</span>
        </dd>
        <dt>Baseline</dt><dd>{plane.baselineAt ? `${plane.baselineKeys || '?'} settings · ${fmtAgo(plane.baselineAt)} ago` : <span className="cs-help">never applied</span>} <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => onBaseline(plane.id)}>Re-apply</button></dd>
      </dl>

      <div className="aa-mail-controls">
        <label className="aa-mail-check">
          <input type="checkbox" checked={!!plane.smartHost?.enabled} disabled={busy} onChange={(e) => onPatch(plane.id, { smartHost: e.target.checked })} />
          <span><strong>SES smart host</strong> <span className="cs-help">relay outbound through SES while the IP is cold (2–4 weeks); flip off once mail-tester ≥ 9 and inbox placement is clean{plane.smartHost?.since ? ` · since ${fmtAgo(plane.smartHost.since)} ago` : ''}</span></span>
        </label>
        <div className="cs-help" style={{ marginTop: 8 }}>Manual steps (tick when done — the verify stage warns until they are):</div>
        {[['port25', 'Port-25 egress unblocked for the EIP(s) (AWS removal request form)'], ['ptr', `PTR record(s) → ${plane.hostname}`], ['tls', 'TLS for 25/465/587/993/443 in Stalwart (ACME or uploaded cert)']].map(([k, label]) => (
          <label key={k} className="aa-mail-check">
            <input type="checkbox" checked={!!cl[k]} disabled={busy} onChange={(e) => onPatch(plane.id, { checklist: { [k]: e.target.checked } })} />
            <span>{label}</span>
          </label>
        ))}
        <div className="aa-launch-form" style={{ marginTop: 8 }}>
          <input type="password" className="aa-input" placeholder={credState?.exists ? 'management credential set — paste user:pass to replace' : 'management credential user:pass (SSM write-only)'} value={cred} onChange={(e) => setCred(e.target.value)} autoComplete="off" />
          <button type="button" className="cs-btn cs-btn-sm" disabled={busy || !cred.includes(':')} onClick={async () => { await onCredential(plane.id, cred); setCred(''); setCredState({ ...(credState || {}), exists: true }); }}>Save</button>
        </div>
        <span className="cs-help">{credState?.param || plane.credParam}</span>
        <div className="aa-launch-form" style={{ marginTop: 8 }}>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => onPatch(plane.id, { status: plane.status === 'active' ? 'draining' : 'active' })}>{plane.status === 'active' ? 'Drain (no new domains)' : 'Activate'}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminMailPlanes({ notify }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [reg, setReg] = useState({ instanceId: '', publicIp: '', warmupIp: '', hostname: '', kind: 'shared', planeId: '', ownerOrgId: '', ownerAppId: '' });
  const [script, setScript] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await apiGet('/apps/admin/mail/planes'));
      setErr('');
    } catch (e) {
      setErr(e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      notify?.(typeof okMsg === 'function' ? okMsg(r) : okMsg);
      await load();
    } catch (e) {
      notify?.(`Failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  const onPatch = (id, patch) => run(() => apiPost(`/apps/admin/mail/planes/${id}`, patch), (r) => `plane ${id} updated${r?.baseline?.applied ? ` · baseline re-applied (${r.baseline.applied} keys)` : r?.baseline?.error ? ` · baseline FAILED: ${r.baseline.error}` : ''}`);
  const onBaseline = (id) => run(() => apiPost(`/apps/admin/mail/planes/${id}/baseline`, {}), (r) => `baseline applied on ${id} (${r.applied} keys)`);
  const onCredential = (id, value) => run(() => apiPost(`/apps/admin/mail/planes/${id}/credential`, { value }), `credential stored for ${id}`);
  const onOverride = (d, state) => run(() => apiPost(`/apps/admin/mail/domains/${d.orgId}/${d.appId}/override`, { state }), `${d.mail.domain}: override ${state || 'cleared'}`);
  const register = (e) => {
    e.preventDefault();
    run(() => apiPost('/apps/admin/mail/planes/register', { ...reg, warmupIp: reg.warmupIp || undefined, planeId: reg.planeId || undefined, ownerOrgId: reg.ownerOrgId || undefined, ownerAppId: reg.ownerAppId || undefined }), (r) => `plane ${r.planeId} registered · DNS ${r.dns?.published || r.dns?.skipped || r.dns?.error || ''}`);
  };

  if (err) return <p className="cs-error">{err}</p>;
  if (!data) return <p className="cs-help">Loading…</p>;
  const cfg = data.config || {};

  return (
    <>
      <h2>Mail planes <span className="cs-help">the Stalwart hosts behind the email service · shared tier included for {(cfg.includedPlans || []).join(' / ')} · dedicated is the add-on</span></h2>
      {cfg.enabled === false && <p className="cs-error">apps.mail.enabled is false — mail cannot be turned on by customers until it is true in config/app.</p>}
      <div className="aa-mail-planes">
        {(data.planes || []).map((p) => <PlaneCard key={p.id} plane={p} busy={busy} onPatch={onPatch} onBaseline={onBaseline} onCredential={onCredential} />)}
        {!data.planes?.length && <p className="cs-help">No mail planes yet. Launch a host, run the bootstrap below, then register it.</p>}
      </div>

      <div className="cs-card" style={{ marginTop: 16 }}>
        <h3>Register a plane</h3>
        <form className="aa-mail-register" onSubmit={register}>
          <input className="aa-input" placeholder="EC2 instance id (i-…)" value={reg.instanceId} onChange={(e) => setReg({ ...reg, instanceId: e.target.value.trim() })} required />
          <input className="aa-input" placeholder="Primary EIP" value={reg.publicIp} onChange={(e) => setReg({ ...reg, publicIp: e.target.value.trim() })} required />
          <input className="aa-input" placeholder="Warm-up EIP (optional)" value={reg.warmupIp} onChange={(e) => setReg({ ...reg, warmupIp: e.target.value.trim() })} />
          <input className="aa-input" placeholder="MX hostname (mx1.mail.nventr.io)" value={reg.hostname} onChange={(e) => setReg({ ...reg, hostname: e.target.value.trim().toLowerCase() })} required />
          <select className="aa-input" value={reg.kind} onChange={(e) => setReg({ ...reg, kind: e.target.value })}>
            <option value="shared">shared</option>
            <option value="dedicated">dedicated</option>
          </select>
          <input className="aa-input" placeholder="plane id (optional)" value={reg.planeId} onChange={(e) => setReg({ ...reg, planeId: e.target.value.trim() })} />
          {reg.kind === 'dedicated' && (
            <>
              <input className="aa-input" placeholder="owner orgId" value={reg.ownerOrgId} onChange={(e) => setReg({ ...reg, ownerOrgId: e.target.value.trim() })} required />
              <input className="aa-input" placeholder="owner appId" value={reg.ownerAppId} onChange={(e) => setReg({ ...reg, ownerAppId: e.target.value.trim() })} required />
            </>
          )}
          <button type="submit" className="cs-btn cs-btn-sm" disabled={busy}>Register</button>
        </form>
        <p className="cs-help">
          Then put the Stalwart admin credential (<code>admin:&lt;ADMIN_PASSWORD&gt;</code>) on the plane card, re-apply the baseline, and work the manual checklist.{' '}
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => apiGet('/apps/admin/mail/host-bootstrap').then((r) => setScript(r.script)).catch((e) => notify?.({ kind: 'err', text: e.message }))}>Show host bootstrap script</button>
        </p>
        {script && <pre className="aa-pre">{script}</pre>}
      </div>

      <div className="cs-card aa-tablewrap" style={{ marginTop: 16 }}>
        <h3>Domains <span className="cs-help">{(data.domains || []).length} with mail · reputation ledger from the hourly monitor</span></h3>
        <table className="aa-table">
          <thead>
            <tr><th>Domain</th><th>App</th><th>Tier / plane</th><th>Status</th><th>Reputation</th><th>24h</th><th>Mailboxes</th><th>Override</th></tr>
          </thead>
          <tbody>
            {(data.domains || []).map((d) => {
              const m = d.mail || {};
              const rep = m.reputation || {};
              return (
                <tr key={`${d.orgId}/${d.appId}`}>
                  <td className="aa-mono">{m.domain}</td>
                  <td>{d.name} <span className="cs-help">{d.slug} · {d.plan}</span></td>
                  <td>{m.tier} <span className="cs-help">{m.planeId || '—'}</span></td>
                  <td><span className={`aa-health-badge is-${m.status === 'live' ? 'up' : m.status === 'paused' ? 'down' : 'warn'}`}>{m.status}</span></td>
                  <td>
                    <span className={`aa-health-badge is-${REP_TONE[rep.state] || 'unknown'}`}>{rep.state || '—'}</span>
                    {rep.override && <span className="cs-help"> pinned by {rep.overrideBy}</span>}
                    {rep.lastReason && <div className="cs-help">{rep.lastReason}</div>}
                  </td>
                  <td className="cs-help">{rep.sendsToday ?? 0} sent · {rep.bounces24h ?? 0} bounced · {((rep.bounceRate || 0) * 100).toFixed(1)}%</td>
                  <td>{m.mailboxes ?? '—'}</td>
                  <td>
                    <span className="aa-launch-form">
                      <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => onOverride(d, 'good')}>Lift</button>
                      <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => onOverride(d, 'paused')}>Pause</button>
                      {rep.override && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => onOverride(d, null)}>Unpin</button>}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!data.domains?.length && <tr><td colSpan={8} className="cs-help">No customer domains yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
